import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeRuntime } from '@/lib/nativeRuntime';
import { supabase } from '@/integrations/supabase/client';

// Paths that notifications must never be allowed to navigate to.
const BLOCKED_PATHS = new Set(['/boot', '/splash', '/auth', '/auth/callback', '/native-callback', '/oauth/auth']);

// Legacy route tokens that don't exist as real routes → map to real ones
const ROUTE_ALIASES: Record<string, string> = {
  scan: 'home',
  upgrade: 'paywall',
  subscribe: 'paywall',
  premium: 'paywall',
  settings: 'settings',
  alerts: 'notifications',
  inbox: 'notifications',
};

const LOCAL_NOTIFS_KEY = 'gistgift_local_notifications';

// TTL-based dedup: same deep-link within 2s is considered duplicate
const DEDUP_TTL_MS = 2000;

interface ParsedDeepLink {
  path: string;
  scanId: string | null;
  source: string | null;
  title: string | null;
  message: string | null;
  notificationType: string | null;
}

/**
 * Cache a notification locally so guest users can see it in the inbox.
 */
function cacheNotificationLocally(title: string, message: string, notificationType: string | null) {
  try {
    const cached = JSON.parse(localStorage.getItem(LOCAL_NOTIFS_KEY) || '[]');
    cached.unshift({
      id: `local_${Date.now()}`,
      title,
      message,
      notification_type: notificationType || 'system',
      deep_link: null,
      sent_at: new Date().toISOString(),
      campaign_step: null,
      campaign_day: null,
    });
    if (cached.length > 50) cached.length = 50;
    localStorage.setItem(LOCAL_NOTIFS_KEY, JSON.stringify(cached));
    console.log('[NotificationHandler] Cached notification locally:', title);
  } catch (e) {
    console.warn('[NotificationHandler] Failed to cache notification:', e);
  }
}

function parseDeepLink(url: string): ParsedDeepLink | null {
  try {
    let normalizedUrl = url.trim();

    const customSchemes = ['gistgift://', 'gistandgift://'];
    for (const scheme of customSchemes) {
      if (normalizedUrl.startsWith(scheme)) {
        normalizedUrl = 'https://app.local/' + normalizedUrl.substring(scheme.length);
        break;
      }
    }

    const parsed   = new URL(normalizedUrl);
    const pathname = parsed.pathname || '/';

    let path             = pathname.replace(/^\/+/, '').replace('app.local/', '');
    const scanId           = parsed.searchParams.get('scanId');
    const source           = parsed.searchParams.get('source');
    const title            = parsed.searchParams.get('t');
    const message          = parsed.searchParams.get('m');
    const notificationType = parsed.searchParams.get('nt');

    // Resolve legacy route aliases
    if (ROUTE_ALIASES[path]) {
      console.log(`[NotificationHandler] Alias "${path}" → "${ROUTE_ALIASES[path]}"`);
      path = ROUTE_ALIASES[path];
    }

    console.log('[NotificationHandler] Parsed deep link:', { original: url, path, scanId, source, hasTitle: !!title });
    return { path, scanId, source, title, message, notificationType };

  } catch (error) {
    console.error('[NotificationHandler] URL parse failed:', url, error);

    try {
      const scanIdMatch = url.match(/scanId=([a-zA-Z0-9-]+)/);
      const sourceMatch = url.match(/source=([a-zA-Z0-9_-]+)/);
      if (scanIdMatch) {
        return {
          path:   'results',
          scanId: scanIdMatch[1],
          source: sourceMatch ? sourceMatch[1] : 'notification',
          title: null, message: null, notificationType: null,
        };
      }
    } catch (regexErr) {
      console.error('[NotificationHandler] Regex fallback failed:', regexErr);
    }

    return null;
  }
}

function isDeepLinkUrl(url: string): boolean {
  return ['gistgift://', 'gistandgift://'].some(s => url.startsWith(s));
}

/**
 * Extract a deep link URL from various OneSignal/Despia notification payload shapes.
 */
function extractDeepLinkFromPayload(data: any): string | null {
  if (!data) return null;
  // Direct properties
  if (data.url && typeof data.url === 'string') return data.url;
  if (data.deep_link && typeof data.deep_link === 'string') return data.deep_link;
  if (data.launchURL && typeof data.launchURL === 'string') return data.launchURL;
  // Nested under .data (OneSignal additional data)
  if (data.data) {
    if (data.data.deep_link) return data.data.deep_link;
    if (data.data.url) return data.data.url;
    if (data.data.launchURL) return data.data.launchURL;
  }
  // Nested under .notification (Despia wrapper)
  if (data.notification) {
    if (data.notification.url) return data.notification.url;
    if (data.notification.launchURL) return data.notification.launchURL;
  }
  // OneSignal .additionalData
  if (data.additionalData?.deep_link) return data.additionalData.deep_link;
  return null;
}

// ── Global callback for Despia/OneSignal native bridge ────────────────────────
if (typeof window !== 'undefined') {
  (window as any).onNotificationOpened = (data: any) => {
    const deepLink = extractDeepLinkFromPayload(data);
    if (deepLink) {
      console.log('[NotificationHandler] 🔔 onNotificationOpened global callback:', deepLink);
      window.dispatchEvent(new CustomEvent('notification-opened', { detail: { url: deepLink } }));
    } else {
      console.log('[NotificationHandler] 🔔 onNotificationOpened — no deep link, defaulting to /notifications');
      window.dispatchEvent(new CustomEvent('notification-opened', { detail: { url: 'gistgift://notifications?source=notification' } }));
    }
  };
  console.log('[NotificationHandler] Global onNotificationOpened callback registered');
}

export const useNotificationHandler = () => {
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();

  const isNative = useMemo(() => isNativeRuntime(), []);
  const [isAppReady, setIsAppReady] = useState(false);

  const hasProcessedInitialUrl = useRef(false);

  // TTL-based dedup: map of urlKey → timestamp of last processing
  const processedTimestamps = useRef(new Map<string, number>());

  useEffect(() => {
    const t = setTimeout(() => setIsAppReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  // ── Core navigation handler ───────────────────────────────────────────────────
  const processDeepLink = useCallback((url: string): boolean => {
    if (!isDeepLinkUrl(url)) {
      console.log('[NotificationHandler] Not a deep link, ignoring:', url);
      return false;
    }

    // ── OAuth return: handle BEFORE dedup and BEFORE BLOCKED_PATHS ───────────
    try {
      const normalized = 'https://app.local/' + url.replace(/^(gistgift|gistandgift):\/\//, '');
      const rawParsed  = new URL(normalized);
      const rawPath    = rawParsed.pathname.replace(/^\/+/, '');

      if (rawPath === 'oauth/auth') {
        const accessToken  = rawParsed.searchParams.get('access_token');
        const refreshToken = rawParsed.searchParams.get('refresh_token');
        const oauthError   = rawParsed.searchParams.get('error');

        if (accessToken && refreshToken) {
          console.log('[NotificationHandler] OAuth return deeplink detected — setting session');
          supabase.auth
            .setSession({ access_token: accessToken, refresh_token: refreshToken })
            .then(({ error: sessionError }) => {
              if (sessionError) {
                console.error('[NotificationHandler] setSession failed:', sessionError.message);
                navigate('/auth', { replace: true });
              } else {
                console.log('[NotificationHandler] Session set successfully — navigating to /home');
                navigate('/home', { replace: true });
              }
            })
            .catch((err) => {
              console.error('[NotificationHandler] setSession threw:', err);
              navigate('/auth', { replace: true });
            });
          return true;
        }

        if (oauthError) {
          console.warn('[NotificationHandler] OAuth return with error:', oauthError);
          navigate('/auth', { replace: true });
          return true;
        }
      }
    } catch (e) {
      console.warn('[NotificationHandler] OAuth return detection failed:', e);
    }

    // ── TTL-based dedup ─────────────────────────────────────────────────────
    const scanIdPart = url.match(/scanId=[^&]+/)?.[0] ?? '';
    const urlKey     = url.split('?')[0] + scanIdPart;
    const now        = Date.now();
    const lastProcessed = processedTimestamps.current.get(urlKey);

    if (lastProcessed && (now - lastProcessed) < DEDUP_TTL_MS) {
      console.log('[NotificationHandler] Dedup (within TTL), skipping:', urlKey);
      return false;
    }

    console.log('[NotificationHandler] Processing deep link:', url);

    const parsed = parseDeepLink(url);
    if (!parsed) {
      console.log('[NotificationHandler] Failed to parse deep link');
      return false;
    }

    const { path, scanId, source, title, message, notificationType } = parsed;

    // Record timestamp for dedup
    processedTimestamps.current.set(urlKey, now);
    // Evict old entries (keep last 50)
    if (processedTimestamps.current.size > 50) {
      const oldest = processedTimestamps.current.keys().next().value;
      if (oldest) processedTimestamps.current.delete(oldest);
    }

    const fullPath = `/${path}`;
    if (BLOCKED_PATHS.has(fullPath) || path.startsWith('oauth/')) {
      console.log('[NotificationHandler] Blocked path — skipping navigation:', fullPath);
      return false;
    }

    // FIX: Inbox-first routing — if source=notification and no scanId, always
    // go to /notifications BEFORE checking any other path branches. This ensures
    // campaign/reengagement/upsell push taps consistently open the Alerts tab.
    if (source === 'notification' && !scanId) {
      if (title || message) {
        cacheNotificationLocally(
          title || 'Notification',
          message || '',
          notificationType,
        );
      }
      console.log('[NotificationHandler] Inbox-first: navigating to /notifications');
      navigate('/notifications', { replace: true });
      return true;
    }

    // Results deep link (notification tapped, go to scan results)
    if (path === 'results' || (scanId && source === 'notification')) {
      console.log('[NotificationHandler] Navigating to results:', scanId);
      try {
        navigate(`/results?scanId=${scanId}`, { replace: true });
        return true;
      } catch (navErr) {
        console.error('[NotificationHandler] Navigation failed:', navErr);
        try {
          window.location.hash = `#/results?scanId=${scanId}`;
          return true;
        } catch (e) {
          console.error('[NotificationHandler] Hash fallback also failed:', e);
        }
      }
    }

    // Notification-list deep link (explicit path)
    if (path === 'notifications') {
      if (title || message) {
        cacheNotificationLocally(
          title || 'Notification',
          message || '',
          notificationType,
        );
      }
      console.log('[NotificationHandler] Navigating to notifications');
      navigate('/notifications', { replace: true });
      return true;
    }

    // Home deep link
    if (path === 'home' || path === '') {
      console.log('[NotificationHandler] Navigating to home');
      navigate('/home', { replace: true });
      return true;
    }

    // Generic path passthrough
    if (path) {
      console.log('[NotificationHandler] Navigating to path:', path);
      navigate(`/${path}`, { replace: true });
      return true;
    }

    return false;
  }, [navigate]);

  // ── Cold-start: check for deep link in the launch URL ────────────────────────
  useEffect(() => {
    if (!isAppReady || hasProcessedInitialUrl.current || !isNative) return;

    hasProcessedInitialUrl.current = true;
    let cancelled = false;

    const timer = setTimeout(async () => {
      if (cancelled) return;

      try {
        const { url: launchUrl } = await CapacitorApp.getLaunchUrl();
        if (!cancelled && launchUrl && isDeepLinkUrl(launchUrl)) {
          console.log('[NotificationHandler] Deep link from getLaunchUrl:', launchUrl);
          processDeepLink(launchUrl);
          return;
        }
      } catch (e) {
        console.warn('[NotificationHandler] getLaunchUrl failed:', e);
      }

      if (cancelled) return;

      const currentUrl = window.location.href;
      if (isDeepLinkUrl(currentUrl)) {
        console.log('[NotificationHandler] Deep link in window.location on launch:', currentUrl);
        processDeepLink(currentUrl);
        return;
      }

      const hash = window.location.hash;
      if (hash && hash.includes('scanId') && hash.includes('source=notification')) {
        console.log('[NotificationHandler] Notification params in hash:', hash);
        const match = hash.match(/scanId=([a-zA-Z0-9-]+)/);
        if (match) navigate(`/results?scanId=${match[1]}`, { replace: true });
      }
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAppReady, isNative, processDeepLink, navigate]);

  // ── Capacitor appUrlOpen ───
  useEffect(() => {
    if (!isAppReady || !isNative) return;

    console.log('[NotificationHandler] Registering appUrlOpen listener');

    let listenerHandle: { remove: () => void } | null = null;
    let cancelled = false;

    const setup = async () => {
      try {
        const handle = await CapacitorApp.addListener('appUrlOpen', (event) => {
          console.log('[NotificationHandler] 📲 appUrlOpen:', event.url);
          processDeepLink(event.url);
        });

        if (cancelled) {
          handle.remove();
        } else {
          listenerHandle = handle;
          console.log('[NotificationHandler] appUrlOpen listener registered');
        }
      } catch (e) {
        console.warn('[NotificationHandler] Could not register appUrlOpen listener:', e);
      }
    };

    setup();

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [isAppReady, isNative, processDeepLink]);

  // ── Despia custom deep link events ───────────────────────────────────────────
  useEffect(() => {
    if (!isAppReady) return;

    const handleDespia = (event: CustomEvent<{ url: string }>) => {
      console.log('[NotificationHandler] Despia deep link event:', event.detail?.url);
      if (event.detail?.url) processDeepLink(event.detail.url);
    };

    window.addEventListener('despia-deeplink' as any, handleDespia);
    return () => window.removeEventListener('despia-deeplink' as any, handleDespia);
  }, [isAppReady, processDeepLink]);

  // ── Global onNotificationOpened callback events ─────────────────────────────
  useEffect(() => {
    if (!isAppReady) return;

    const handleNotificationOpened = (event: CustomEvent<{ url: string }>) => {
      console.log('[NotificationHandler] 🔔 notification-opened event:', event.detail?.url);
      if (event.detail?.url) processDeepLink(event.detail.url);
    };

    window.addEventListener('notification-opened' as any, handleNotificationOpened);
    return () => window.removeEventListener('notification-opened' as any, handleNotificationOpened);
  }, [isAppReady, processDeepLink]);

  // ── Web / fallback: URL search params ────────────────────────────────────────
  useEffect(() => {
    if (!isAppReady) return;

    const scanId = searchParams.get('scanId');
    const source = searchParams.get('source');

    if (scanId && source === 'notification') {
      console.log('[NotificationHandler] URL params → navigating to results:', scanId);

      if (window.location.hash.includes('/results') && window.location.hash.includes(scanId)) {
        console.log('[NotificationHandler] Already on correct results page');
        return;
      }

      try {
        navigate(`/results?scanId=${scanId}`, { replace: true });
      } catch (err) {
        console.error('[NotificationHandler] URL param navigation failed:', err);
      }
    }
  }, [isAppReady, searchParams, navigate]);

  // ── On resume: re-check window.location for pending deep links ───────────────
  useEffect(() => {
    if (!isAppReady || !isNative) return;

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      setTimeout(() => {
        const currentUrl = window.location.href;
        if (isDeepLinkUrl(currentUrl)) {
          console.log('[NotificationHandler] Deep link on resume:', currentUrl);
          processDeepLink(currentUrl);
        }
      }, 100);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isAppReady, isNative, processDeepLink]);
};
